// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = "/monitoring";globalThis["SENTRY_RELEASE"] = {"id":"4519f3ad76468f57e85600ce34bf4d986f5439b7"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";import * as Sentry from "@sentry/nextjs";
import {
  applySentryDeploymentTags,
  getSentryEnvironment,
  getSentryTracesSampleRate,
} from "./src/lib/sentryEnvironment";
import {isNavigationCancelledError} from "./src/lib/navigationCancelled";
import {isGpcActive, parseConsentCookie} from "./src/lib/consentCookie";
import {installChunkLoadRecovery} from "./src/lib/chunkLoadRecovery";
import {shouldIgnoreReplayHydrationNoise} from "./src/lib/sentryHydrationNoise";
import {
  shouldIgnoreAnonymousPermissionDenied,
  shouldIgnoreBrowserIndexedDbNoSpaceNoise,
  shouldIgnoreFirebaseAuthPendingPromise,
  shouldIgnoreFirebaseTransientNetworkError,
  shouldIgnoreGenericNetworkLoadFailure,
  shouldIgnoreOfficeAddinBridgeNoise,
  shouldIgnoreStaleChunkParseError,
  shouldIgnoreVidstackDetachedMediaError,
} from "./src/lib/sentryClientIgnore";
import "./src/lib/installSafeWebStorage";
import {
  SENTRY_REPLAY_HOSTNAME,
  shouldEnableSentryReplay,
} from "./src/lib/sentryReplayConsent";

const KNOWN_HISTORY_WRAPPER_PATTERNS = [
  "beacon",
  "clarity",
  "googletagmanager",
  "google-analytics",
  "gtag/js",
  "bat.js",
];

function isKnownHistoryWrapperFrame(filename: string): boolean {
  const normalizedFilename = filename.toLowerCase();
  return KNOWN_HISTORY_WRAPPER_PATTERNS.some((pattern) =>
    normalizedFilename.includes(pattern),
  );
}

function shouldIgnoreReadonlyHistoryBeaconError(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const eventMessage = event.exception?.values?.[0]?.value || "";
  const originalMessage =
    hint.originalException instanceof Error ? hint.originalException.message : "";
  const message = `${eventMessage} ${originalMessage}`.toLowerCase();

  if (!message.includes("cannot assign to read only property 'pushstate'")) {
    return false;
  }

  const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
  return frames.some((frame) => isKnownHistoryWrapperFrame(frame.filename || ""));
}

function shouldIgnoreVidstackGestureNullSetAttributeError(
  event: Sentry.Event,
  hint: Sentry.EventHint,
): boolean {
  const eventMessage = event.exception?.values?.[0]?.value || "";
  const originalMessage =
    hint.originalException instanceof Error ? hint.originalException.message : "";
  const message = `${eventMessage} ${originalMessage}`.toLowerCase();

  if (!message.includes("cannot read properties of null (reading 'setattribute')")) {
    return false;
  }

  const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
  return frames.some((frame) => {
    const filename = (frame.filename || "").toLowerCase();
    const functionName = (frame.function || "").toLowerCase();
    return filename.includes("vidstack") && functionName.includes("gesture");
  });
}

function getCombinedExceptionMessage(event: Sentry.Event, hint: Sentry.EventHint): string {
  const exceptionValues = event.exception?.values || [];
  const eventMessages = exceptionValues
    .map((exception) => `${exception.type || ""} ${exception.value || ""}`)
    .join(" ");
  const originalMessage =
    hint.originalException instanceof Error ? hint.originalException.message : "";
  const primitiveException =
    typeof hint.originalException === "string" ? hint.originalException : "";

  return `${eventMessages} ${originalMessage} ${primitiveException}`.toLowerCase();
}

function getExceptionFrames(event: Sentry.Event) {
  return (event.exception?.values || []).flatMap(
    (exception) => exception.stacktrace?.frames || [],
  );
}

function getEventUrl(event: Sentry.Event): string {
  const taggedUrl = event.tags?.url;
  return String(event.request?.url || taggedUrl || "").toLowerCase();
}

function getEventBreadcrumbs(event: Sentry.Event): Array<{
  category?: string | null;
  message?: string | null;
}> {
  return (event.breadcrumbs || []).map((breadcrumb) => ({
    category: breadcrumb.category,
    message: breadcrumb.message,
  }));
}

function shouldIgnoreReplayHydrationEvent(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  return shouldIgnoreReplayHydrationNoise({
    message: `${getCombinedExceptionMessage(event, hint)} ${event.message || ""} ${event.logentry?.message || ""}`,
    title: event.transaction,
    url: getEventUrl(event),
    breadcrumbs: getEventBreadcrumbs(event),
  });
}

function shouldIgnoreLocalDevelopmentEvent(event: Sentry.Event): boolean {
  const url = getEventUrl(event);
  return (
    url.startsWith("http://localhost") ||
    url.startsWith("https://localhost") ||
    url.startsWith("http://127.0.0.1") ||
    url.startsWith("https://127.0.0.1")
  );
}

function hasInjectedAddEventListenerHook(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const frames = getExceptionFrames(event);
  const rawStack = getRawErrorStack(hint);
  if (rawStack.includes("addel_hook")) {
    return true;
  }
  return frames.some((frame) => (frame.function || "").toLowerCase().includes("addel_hook"));
}

function shouldIgnoreThirdPartyExtensionNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  const frames = getExceptionFrames(event);

  if (
    message.includes("failed to connect to metamask") ||
    message.includes("metamask extension not found")
  ) {
    return frames.some((frame) => (frame.filename || "").toLowerCase().includes("scripts/inpage.js"));
  }

  // Injected addEventListener wrappers (addEL_hook) read target.tagName on a
  // null node while PostHog web-vitals or Stripe attach listeners
  // (Sentry JAVASCRIPT-NEXTJS-1KA / JAVASCRIPT-NEXTJS-1K7).
  const isNullTagNameRead =
    message.includes("tagname") &&
    (
      message.includes("cannot read properties of null") ||
      message.includes("null is not an object")
    );
  if (isNullTagNameRead && hasInjectedAddEventListenerHook(event, hint)) {
    return true;
  }

  return false;
}

function shouldIgnoreFirebaseAnalyticsDynamicConfigFetch(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  return shouldIgnoreGenericNetworkLoadFailure(event, hint);
}

function isPostHogRecorderFilename(filename: string): boolean {
  const f = filename.toLowerCase();
  return (
    f.includes("posthog-recorder") ||
    f.includes("posthog.com") ||
    f.includes("i.posthog.com")
  );
}

/**
 * PostHog session recorder POSTs can reject as unhandled TypeError: Failed to
 * fetch when an extension wraps window.fetch (e.g. frame_ant.js) or the
 * ingest host is blocked. Stack is only recorder + Sentry fetch wrap — no
 * Braiv frames (Sentry JAVASCRIPT-NEXTJS-1J5).
 */
function shouldIgnorePostHogRecorderFetchNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  if (!message.includes("failed to fetch")) return false;

  const frames = getExceptionFrames(event);
  const rawStack = getRawErrorStack(hint);
  const hasPostHogEvidence =
    frames.some((frame) => isPostHogRecorderFilename(frame.filename || "")) ||
    isPostHogRecorderFilename(rawStack);
  if (!hasPostHogEvidence) return false;

  return !frames.some((frame) => {
    const filename = (frame.filename || "").toLowerCase();
    if (!filename || isPostHogRecorderFilename(filename)) return false;
    if (filename.includes("node_modules") || filename.includes("@sentry")) return false;
    if (filename.includes("frame_ant")) return false;
    return (
      filename.includes("/src/") ||
      filename.includes("_next/static/chunks/") ||
      filename.includes("pages/") ||
      filename.includes("app/")
    );
  });
}

function isInjectedFetchWrapperFilename(filename: string): boolean {
  const f = filename.toLowerCase();
  return (
    f.includes("executors/200.js") ||
    f.includes("frame_ant") ||
    f.includes("fbevents.js") ||
    f.includes("connect.facebook.net")
  );
}

/**
 * Meta Pixel / extension scripts patch window.fetch and throw
 * `Cannot read properties of undefined (reading 'M_ID')` during our
 * Firebase callable (Sentry JAVASCRIPT-NEXTJS-1J4). The edit hook already
 * catches the callable; the inject's extra promise is unhandled.
 */
function shouldIgnoreInjectedFetchWrapperNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  const frames = getExceptionFrames(event);
  const rawStack = getRawErrorStack(hint);
  const hasWrapperEvidence =
    frames.some((frame) => isInjectedFetchWrapperFilename(frame.filename || "")) ||
    isInjectedFetchWrapperFilename(rawStack);

  if (message.includes("reading 'm_id'") || message.includes('reading "m_id"')) {
    return true;
  }

  return hasWrapperEvidence && message.includes("failed to fetch");
}

function shouldIgnoreFirestoreOfflineNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  return message.includes("failed to get document because the client is offline");
}

/**
 * Firestore JS 10.x throws `INTERNAL ASSERTION FAILED: Unexpected state` when
 * onSnapshot unsubscribe runs after the async queue has already failed or
 * shut down (Sentry JAVASCRIPT-NEXTJS-1JJ). Runtime cleanup swallows this;
 * leftover SDK throws are still noise.
 */
function shouldIgnoreFirestoreUnexpectedStateNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  return message.includes("firestore") &&
    message.includes("internal assertion failed") &&
    message.includes("unexpected state");
}

function shouldIgnoreBrowserIndexedDbResetNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  return message.includes("database deleted by request of the user");
}

function getRawErrorStack(hint: Sentry.EventHint): string {
  const original = hint.originalException;
  if (original instanceof Error && typeof original.stack === "string") {
    return original.stack.toLowerCase();
  }
  return "";
}

function shouldIgnoreVidstackProviderLifecycleNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  const frames = getExceptionFrames(event);
  const rawStack = getRawErrorStack(hint);
  const mechanism = event.exception?.values?.[0]?.mechanism?.type || "";

  const hasVidstackEvidence =
    frames.some((frame) => (frame.filename || "").toLowerCase().includes("vidstack")) ||
    rawStack.includes("vidstack");

  if (message.includes("provider destroyed")) {
    return true;
  }

  const isNullPropertyRead =
    message.includes("cannot read properties of null") ||
    message.includes("null is not an object");
  const isLifecycleProperty =
    message.includes("addeventlistener") ||
    message.includes("setattribute") ||
    message.includes("waiting") ||
    message.includes("this.media.load is not a function");

  if (!isNullPropertyRead || !isLifecycleProperty) {
    return false;
  }

  if (hasVidstackEvidence) {
    return true;
  }

  // Parsed frames are sometimes empty for `onunhandledrejection`; require Vidstack in the raw stack.
  return mechanism === "onunhandledrejection" && rawStack.includes("vidstack");
}

function shouldIgnoreResizeObserverLoopNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  return message.includes("resizeobserver loop limit exceeded");
}

function shouldIgnoreCrossOriginFrameAccessNoise(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  return message.includes("blocked a frame with origin") &&
    message.includes("protocols, domains, and ports must match");
}

/**
 * Firebase Auth BrowserLocalPersistence polls localStorage every ~2s on mobile.
 * iOS Google in-app browsers can expose a null storage object after init
 * (Sentry JAVASCRIPT-NEXTJS-138). Runtime persistence fallback exists; remaining
 * poll ticks are SDK noise, not app failures.
 */
function shouldIgnoreFirebaseAuthLocalStoragePollerNoise(
  event: Sentry.Event,
  hint: Sentry.EventHint,
): boolean {
  const message = getCombinedExceptionMessage(event, hint);
  const isNullStorageGetItem =
    message.includes("this.storage.getitem") ||
    (message.includes("storage.getitem") && (
      message.includes("null is not an object") ||
      message.includes("cannot read properties of null")
    ));
  if (!isNullStorageGetItem) return false;

  const frames = getExceptionFrames(event);
  const rawStack = getRawErrorStack(hint);
  const mentionsAuthPersistence =
    frames.some((frame) => {
      const filename = (frame.filename || "").toLowerCase();
      const functionName = (frame.function || "").toLowerCase();
      return (
        filename.includes("@firebase/auth") &&
        (
          functionName.includes("forallchangedkeys") ||
          functionName.includes("browserlocalpersistence") ||
          functionName.includes("polltimer")
        )
      );
    }) ||
    rawStack.includes("browserlocalpersistence") ||
    rawStack.includes("forallchangedkeys");
  return mentionsAuthPersistence;
}

function isReplayHost(): boolean {
  if (typeof window === "undefined") return false;

  // Sentry Replay walks the DOM and can trigger noisy browser console errors
  // when secure third-party iframes are present (Stripe, Firebase Auth, etc.).
  // Keep Replay on the production app host, but avoid it on dev/staging domains.
  return window.location.hostname === SENTRY_REPLAY_HOSTNAME;
}

const replayHost = isReplayHost();
const sentryEnvironment = getSentryEnvironment();

// GDPR/ePrivacy split: error and issue tracking below runs without consent
// (strictly necessary service-integrity monitoring; no cookies or persistent
// identifiers). Session Replay is "Session Recording" under the Braiv cookie
// policy and requires explicit consent, so its integration is NOT registered
// here — it is added lazily by syncReplayWithConsent() once the braiv_consent
// cookie grants the `recording` flag (mirroring the PostHog opt-in flow).
Sentry.init({
  dsn: "https://f8a7c113b531d6596220f21e0712fe33@o4508256885276672.ingest.us.sentry.io/4508256895041536",

  integrations: [],

  // Early drop for Vidstack provider teardown races (often missing parsed frames on rejections)
  // and transient Firebase offline/network codes. beforeSend also matches FirebaseError.code
  // when the message does not include the code.
  ignoreErrors: [
    "Provider destroyed",
    /Cannot read properties of null \(reading '(addEventListener|setAttribute|waiting)'\)/i,
    /Cannot read properties of undefined \(reading 'M_ID'\)/i,
    /null is not an object \(evaluating 'this\.storage\.getItem'\)/i,
    /FIRESTORE .*INTERNAL ASSERTION FAILED: Unexpected state/i,
    /Pending promise was never set/i,
    /Object Not Found Matching Id:\d+/i,
    /canPlayType is not a function/i,
    /media\.load is not a function/i,
    /FILE_ERROR_NO_SPACE/i,
    /auth\/network-request-failed/i,
    /installations\/app-offline/i,
  ],

  tracesSampleRate: getSentryTracesSampleRate(sentryEnvironment),

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production.
  // Rates only take effect once the replay integration is added after consent.
  replaysSessionSampleRate: replayHost ? 0.1 : 0,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: replayHost ? 1.0 : 0,

  beforeSend(event, hint) {
    if (isNavigationCancelledError(hint.originalException)) {
      return null;
    }

    applySentryDeploymentTags({
      setTag: (key, value) => {
        event.tags = event.tags || {};
        event.tags[key] = value;
      },
    });

    // Do not mix local developer repros into production triage.
    if (shouldIgnoreLocalDevelopmentEvent(event)) {
      return null;
    }

    // Drop known third-party beacon noise from production analytics scripts.
    if (shouldIgnoreReadonlyHistoryBeaconError(event, hint)) {
      return null;
    }

    // Drop known Vidstack gesture teardown race while runtime mitigation is active.
    if (shouldIgnoreVidstackGestureNullSetAttributeError(event, hint)) {
      return null;
    }

    if (
      shouldIgnoreThirdPartyExtensionNoise(event, hint) ||
      shouldIgnorePostHogRecorderFetchNoise(event, hint) ||
      shouldIgnoreInjectedFetchWrapperNoise(event, hint) ||
      shouldIgnoreFirebaseAuthLocalStoragePollerNoise(event, hint) ||
      shouldIgnoreFirebaseTransientNetworkError(event, hint) ||
      shouldIgnoreFirebaseAnalyticsDynamicConfigFetch(event, hint) ||
      shouldIgnoreFirestoreOfflineNoise(event, hint) ||
      shouldIgnoreFirestoreUnexpectedStateNoise(event, hint) ||
      shouldIgnoreBrowserIndexedDbResetNoise(event, hint) ||
      shouldIgnoreBrowserIndexedDbNoSpaceNoise(event, hint) ||
      shouldIgnoreVidstackProviderLifecycleNoise(event, hint) ||
      shouldIgnoreVidstackDetachedMediaError(event, hint) ||
      shouldIgnoreFirebaseAuthPendingPromise(event, hint) ||
      shouldIgnoreOfficeAddinBridgeNoise(event, hint) ||
      shouldIgnoreGenericNetworkLoadFailure(event, hint) ||
      shouldIgnoreAnonymousPermissionDenied(event, hint, getEventUrl(event), event.user?.id) ||
      shouldIgnoreStaleChunkParseError(event, hint) ||
      shouldIgnoreResizeObserverLoopNoise(event, hint) ||
      shouldIgnoreCrossOriginFrameAccessNoise(event, hint) ||
      shouldIgnoreReplayHydrationEvent(event, hint)
    ) {
      return null;
    }

    return event;
  },

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  environment: sentryEnvironment,
});

// Drop Vidstack lifecycle noise before other integrations fingerprint / attach replay-with-error.
Sentry.addEventProcessor((event, hint) => {
  if (shouldIgnoreVidstackProviderLifecycleNoise(event, hint)) {
    return null;
  }
  if (shouldIgnoreReplayHydrationEvent(event, hint)) {
    return null;
  }
  return event;
});

// --- Consent-gated Session Replay -----------------------------------------
//
// Replay is only started when the braiv_consent cookie grants the `recording`
// flag (written by the consent banner / geo bootstrap in _app.tsx) and GPC is
// inactive. `/embed/*` never records, even with a leftover studio cookie —
// anonymous LMS/crawler traffic created JAVASCRIPT-NEXTJS-69 hydration noise.
// Error tracking above is unaffected.

function buildReplayIntegration() {
  return Sentry.replayIntegration({
    // Skip cross-origin third-party iframes (Stripe fraud-detection, Firebase
    // Auth helper). Replay can never read them anyway, and each attempted
    // access makes Safari spam "Blocked a frame with origin" console errors.
    block: [
      'iframe[src*="js.stripe.com"]',
      'iframe[src*="m.stripe.com"]',
      'iframe[src*="auth.app.braiv.co"]',
    ],
  });
}

let replayIntegrationAdded = false;
let replayStoppedByRevocation = false;

function syncReplayWithConsent(): void {
  if (typeof window === "undefined") return;

  const enable = shouldEnableSentryReplay({
    hostname: window.location.hostname,
    gpcActive: isGpcActive(),
    recordingConsent: !!parseConsentCookie()?.recording,
    pathname: window.location.pathname,
  });

  try {
    if (enable) {
      if (!replayIntegrationAdded) {
        replayIntegrationAdded = true;
        Sentry.addIntegration(buildReplayIntegration());
      } else if (replayStoppedByRevocation) {
        // Consent re-granted mid-session after a revocation.
        replayStoppedByRevocation = false;
        void Sentry.getReplay()?.start();
      }
      return;
    }

    if (replayIntegrationAdded && !replayStoppedByRevocation) {
      // Consent revoked mid-session: stop recording immediately.
      replayStoppedByRevocation = true;
      void Sentry.getReplay()?.stop();
    }
  } catch {
    // Replay lifecycle must never break the app; worst case is no recording.
  }
}

if (typeof window !== "undefined") {
  // Recover from stale Next.js chunks (post-deploy / flaky crawlers) before
  // Replay can mis-label the failed hydrate as JAVASCRIPT-NEXTJS-69.
  installChunkLoadRecovery();
  // Initial check covers returning visitors whose cookie already grants
  // recording (previous behaviour for consented users is unchanged).
  syncReplayWithConsent();
  // Re-evaluate whenever the banner, GPC bootstrap, or geo bootstrap emits a
  // consent decision — the same event PostHog consumes in _app.tsx.
  document.addEventListener("braiv:consent", syncReplayWithConsent);
}
